import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  useEditWbsConfiguration,
  useRetrieveCurrentPlanzone,
  useRetrieveWbsConfiguration,
  useRolesRetrieval,
} from "@planzone/view-models";
import { Id, WbsConfiguration as Config } from "@planzone/dto";
import { events } from "@planzone/events";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { HorizontalList } from "@common/horizontalList";
import { useMessage } from "@components/useMessage";
import { Form } from "./form";
import { Props } from "./types";
import styles from "./styles.scss";

function canSubmitForm(profile: Config[number]) {
  return profile.roles.length > 0 && profile.columns.length > 0;
}

function WbsConfiguration({ projectId, cancel }: Props) {
  const { t } = useTranslation();
  const { roles } = useRolesRetrieval();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { configuration } = useRetrieveWbsConfiguration(planzone);
  const { isRequestPending: isEditionRequestPending, editWbsConfiguration } =
    useEditWbsConfiguration(planzone);
  const [formData, setFormData] = React.useState(configuration);
  const [profileToAdd, setProfileToAdd] = React.useState<Config[number] | null>(
    null,
  );
  const [currentProfile, setCurrentProfile] = React.useState(0);
  const usedRoles = formData
    .reduce<Id[]>((roles, profile) => [...roles, ...profile.roles], [])
    .filter((role) =>
      !profileToAdd && formData[currentProfile]
        ? !formData[currentProfile].roles.find((r) => r === role)
        : true,
    );
  const remainingRoles = roles.filter(
    (role) =>
      ![
        ...(formData[currentProfile] ? formData[currentProfile].roles : []),
        ...usedRoles,
      ].find((id) => id === role.name || role.uuid === id),
  );
  const canSubmit = canSubmitForm(
    !!profileToAdd
      ? profileToAdd
      : !!formData[currentProfile]
        ? formData[currentProfile]
        : {
            name: "",
            roles: [],
            columns: [],
          },
  );

  useMessage(
    ({ type, data }) => {
      if (type === events.editWbsConfigurationSucceeded) {
        setProfileToAdd(null);
        setCurrentProfile(data.configuration.length - 1);
        cancel();
      }
    },
    { localOnly: true },
  );

  React.useEffect(() => {
    setFormData(configuration);
  }, [configuration]);

  return (
    <Modal header={t("wbsConfiguration.title")} close={cancel}>
      <HorizontalList>
        <div className={styles.tabs}>
          {remainingRoles.length > 0 && (
            <p
              className={classNames(styles.tab, {
                [styles.selectedTab]: !!profileToAdd,
              })}
              onClick={() =>
                setProfileToAdd({
                  name: remainingRoles
                    .map(({ name }) => t(`roles.${name}`, name))
                    .join(" & "),
                  roles: remainingRoles.map(({ name }) => name),
                  columns: [
                    { key: "statuses" },
                    { key: "tags" },
                    { key: "allocations" },
                    { key: "startDate" },
                    { key: "endDate" },
                    { key: "completionDate" },
                  ],
                })
              }
            >
              + {t("wbsConfiguration.addProfile")}
            </p>
          )}
          {formData.map(({ name }, index) => (
            <p
              key={index}
              className={classNames(styles.tab, {
                [styles.selectedTab]: index === currentProfile && !profileToAdd,
              })}
              onClick={() => {
                setProfileToAdd(null);
                setCurrentProfile(index);
              }}
            >
              {name}
            </p>
          ))}
        </div>
      </HorizontalList>
      <form
        className={styles.wbsConfiguration}
        onSubmit={(e) => {
          e.preventDefault();

          !isEditionRequestPending &&
            editWbsConfiguration({
              planzone,
              configuration,
              nextConfiguration: !profileToAdd
                ? formData
                : [...formData, profileToAdd],
            });
        }}
      >
        {!!formData[currentProfile] && !profileToAdd && (
          <div className={styles.form}>
            <Form
              projectId={projectId}
              formData={formData[currentProfile]}
              usedRoles={usedRoles}
              edit={(profile) =>
                setFormData([
                  ...formData.slice(0, currentProfile),
                  profile,
                  ...formData.slice(currentProfile + 1),
                ])
              }
            />
          </div>
        )}
        {!!profileToAdd && (
          <div className={styles.form}>
            <Form
              projectId={projectId}
              formData={profileToAdd}
              usedRoles={usedRoles}
              edit={(profile) => setProfileToAdd(profile)}
            />
          </div>
        )}
        <div className={styles.footer}>
          {!profileToAdd && (
            <div>
              <Button
                type="delete"
                status={isEditionRequestPending ? "pending" : "none"}
                isDisabled={isEditionRequestPending}
                press={() => {
                  editWbsConfiguration({
                    planzone,
                    configuration,
                    nextConfiguration: [
                      ...configuration.slice(0, currentProfile),
                      ...configuration.slice(currentProfile + 1),
                    ],
                  });
                  setCurrentProfile(0);
                }}
              >
                {t("wbsConfiguration.remove")}
              </Button>
            </div>
          )}
          <div>
            <Button type="cancel" press={cancel}>
              {t("wbsConfiguration.cancel")}
            </Button>
            <Button
              type="submit"
              status={isEditionRequestPending ? "pending" : "none"}
              isDisabled={isEditionRequestPending || !canSubmit}
            >
              {!profileToAdd
                ? t("wbsConfiguration.edit")
                : t("wbsConfiguration.add")}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export { WbsConfiguration };
